import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Pause, Play, X, RotateCcw, Undo2, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  processing: "bg-blue-100 text-blue-700", paused: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700", cancelled: "bg-slate-200 text-slate-600",
  rolled_back: "bg-slate-200 text-slate-600", queued: "bg-blue-50 text-blue-600",
};
const TYPE_LABEL = { meta: "Missing meta descriptions" };

export default function JobsMonitor() {
  const [jobs, setJobs] = useState(null);
  const timer = useRef(null);
  const load = useCallback(() => {
    api.get("/admin/seo/jobs").then((r) => setJobs(r.data.items)).catch(() => setJobs([]));
  }, []);
  useEffect(() => {
    load();
    timer.current = setInterval(load, 3000);
    return () => clearInterval(timer.current);
  }, [load]);

  const act = async (id, action) => {
    if (action === "cancel" && !window.confirm("Cancel this job? Processed products keep their changes.")) return;
    if (action === "rollback" && !window.confirm("Roll back every product changed by this job to its previous meta/title?")) return;
    try { const r = await api.post(`/admin/seo/jobs/${id}/${action}`); toast.success(r.data.restored != null ? `Rolled back ${r.data.restored} products` : "Done"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  if (!jobs) return <p className="text-sm text-muted-foreground">Loading jobs…</p>;
  if (jobs.length === 0) return (
    <div className="rounded-xl border bg-white p-10 text-center text-sm text-muted-foreground">
      No background jobs yet. Launch one from any Issues table (e.g. “AI Generate missing meta”).
    </div>
  );
  return (
    <div className="space-y-3" data-testid="jobs-monitor">
      {jobs.map((j) => {
        const pct = j.total ? Math.round(((j.completed + j.failed) / j.total) * 100) : 0;
        const live = j.status === "processing" || j.status === "paused";
        return (
          <div key={j.id} className="rounded-xl border bg-white p-4" data-testid={`job-${j.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-steel">{TYPE_LABEL[j.type] || j.type}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[j.status] || "bg-slate-100"}`}>{j.status}</span>
                {j.status === "processing" && <Loader2 size={13} className="animate-spin text-blue-600" />}
              </div>
              <div className="flex items-center gap-1.5">
                {j.status === "processing" && <Button size="sm" variant="outline" onClick={() => act(j.id, "pause")} data-testid={`job-pause-${j.id}`}><Pause size={13} className="mr-1" /> Pause</Button>}
                {j.status === "paused" && <Button size="sm" variant="outline" onClick={() => act(j.id, "resume")} data-testid={`job-resume-${j.id}`}><Play size={13} className="mr-1" /> Resume</Button>}
                {live && <Button size="sm" variant="outline" onClick={() => act(j.id, "cancel")}><X size={13} className="mr-1" /> Cancel</Button>}
                {j.failed > 0 && <Button size="sm" variant="outline" onClick={() => act(j.id, "retry-failed")}><RotateCcw size={13} className="mr-1" /> Retry failed</Button>}
                {(j.status === "completed" || j.status === "cancelled") && j.completed > 0 && <Button size="sm" variant="outline" onClick={() => act(j.id, "rollback")}><Undo2 size={13} className="mr-1" /> Roll back</Button>}
              </div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>Total <b className="text-steel">{j.total.toLocaleString()}</b></span>
              <span className="text-emerald-600"><CheckCircle2 size={11} className="inline mb-0.5" /> Completed <b>{j.completed.toLocaleString()}</b></span>
              <span>Remaining <b className="text-steel">{(j.remaining ?? 0).toLocaleString()}</b></span>
              {j.failed > 0 && <span className="text-red-600"><AlertTriangle size={11} className="inline mb-0.5" /> Failed <b>{j.failed}</b></span>}
              {j.ai_estimate != null && <span>AI calls ~<b className="text-steel">{j.ai_estimate.toLocaleString()}</b></span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
